/**
 * Noticing that the session has ended, from anywhere.
 *
 * **The problem this solves.** Every endpoint except the health checks and the three in
 * `AuthEndpoints` is behind the fallback policy, so a request made after the session has
 * expired comes back `401` with no body. Each screen's own error handling would report that
 * as "unavailable" — and during an outage a clinician who is told the system is not
 * responding rings the on-call technician, when what they needed was to log in again. The
 * two failures look identical to a screen and are nothing alike to a person.
 *
 * So the screens do not each have to know. A 401 on any request is reported here, and
 * `SessionGate` — the one component that owns whether anybody is signed in — listens and
 * drops back to the login screen with a message saying what happened.
 *
 * **Module state rather than a context**, because the reporters are not components: they are
 * the `*Api.ts` modules, which are deliberately plain functions with no React in them so they
 * can be tested without a DOM. A context would mean passing a callback into every fetch.
 *
 * **It never signs anybody out.** It reports a fact the server has already decided — the
 * cookie was absent, expired or undecryptable — and the only thing it changes is what the
 * frontend shows. There is nothing here that can end a session that is still valid.
 */

type Listener = () => void

const listeners = new Set<Listener>()

/**
 * Subscribes to "the session has ended". Returns the unsubscribe.
 *
 * Intended for exactly one subscriber, `SessionGate`, but a Set rather than a single slot so
 * that React mounting a component twice in development does not leave the first registration
 * overwritten and the second one orphaned.
 */
export function onUnauthenticated(listener: Listener): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

/**
 * Reports that a request was answered `401`.
 *
 * Safe to call more than once — the gate is already showing the login screen by the second
 * one, and dropping to a screen you are already on is not an event.
 */
export function reportUnauthenticated(): void {
  for (const listener of listeners) {
    listener()
  }
}

/**
 * Whether a response says the session has ended, reporting it if so.
 *
 * The one line every `*Api.ts` module adds to its non-2xx handling. It returns a boolean
 * rather than throwing so that each caller still throws its own error type: the screen that
 * made the request has a moment before it unmounts, and it must not be left rendering
 * something half-updated.
 *
 * **Not for `/api/auth/session` itself**, where a 401 is the ordinary answer to "who is
 * signed in" and reporting it would be the gate telling itself what it already knows.
 */
export function unauthenticated(response: Response): boolean {
  if (response.status !== 401) {
    return false
  }

  reportUnauthenticated()
  return true
}
